import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import FlowDashboard from './components/FlowDashboard';
import FlowEditor from './components/FlowEditor';
import LeadsDashboard from './components/LeadsDashboard';
import CatalogEditor from './components/CatalogEditor';
import LogsDashboard from './components/LogsDashboard';
import SalesDashboard from './components/SalesDashboard';
import ChatDashboard from './components/ChatDashboard';
import CRMBoard from './components/CRMBoard';
import WarRoomDashboard from './components/WarRoomDashboard';
import Header from './components/Header';
import LoadingSpinner from './components/LoadingSpinner';
import { FlowAPI } from './services/api';

function App() {
  const [isApiOnline, setIsApiOnline] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Inicializa dark mode a partir do localStorage
  useEffect(() => {
    const saved = localStorage.getItem('darkMode') === 'true';
    document.documentElement.classList.toggle('dark', saved);
  }, []);

  useEffect(() => {
    checkApiHealth();
  }, []);

  const checkApiHealth = async () => {
    setIsLoading(true);
    try {
      const isOnline = await FlowAPI.healthCheck();
      setIsApiOnline(isOnline);
      
      if (!isOnline) {
        toast.error('API não está respondendo. Verifique se o servidor backend está executando.', {
          duration: 6000,
        });
      }
    } catch (error) {
      console.error('Erro ao verificar API:', error);
      setIsApiOnline(false);
      toast.error('Erro ao conectar com a API');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Verificando conexão com API...</p>
        </div>
      </div>
    );
  }

  if (isApiOnline === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            API Indisponível
          </h2>
          
          <p className="text-gray-600 mb-6">
            Não foi possível conectar-se ao servidor backend. 
            Verifique se o serviço está executando na porta 8001.
          </p>
          
          <div className="space-y-3">
            <button
              onClick={checkApiHealth}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Tentar Novamente
            </button>
            
            <div className="text-sm text-gray-500">
              <p>Para iniciar o backend, execute:</p>
              <code className="block mt-1 p-2 bg-gray-100 rounded text-xs">
                cd flow_builder/api && python -m uvicorn flow_api:app --port 8001 --reload
              </code>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<FlowDashboard />} />
            <Route path="/leads" element={<LeadsDashboard />} />
            <Route path="/catalog" element={<CatalogEditor />} />
            <Route path="/logs" element={<LogsDashboard />} />
            <Route path="/sales" element={<SalesDashboard />} />
            <Route path="/chat" element={<ChatDashboard />} />
            <Route path="/crm" element={<CRMBoard />} />
            <Route path="/war-room" element={<WarRoomDashboard />} />
            <Route path="/flow/:flowId" element={<FlowEditor />} />
            <Route path="/flow/:flowId/edit" element={<FlowEditor />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        {/* Footer */}
        <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-6 mt-16">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-600 dark:text-gray-400 text-sm">
                © 2026 MelBot -  Controle CPM
              </div>
              
              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${isApiOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-sm text-gray-600">
                    API {isApiOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
                
                <div className="text-sm text-gray-500">
                  v1.0.0
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;